import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../config';

const GIORNI = [['lun', 'Lunedì'], ['mar', 'Martedì'], ['mer', 'Mercoledì'], ['gio', 'Giovedì'],
                ['ven', 'Venerdì'], ['sab', 'Sabato'], ['dom', 'Domenica']];
const GIORNO_DEFAULT = { attivo: true, dalle: '08:00', alle: '20:00' };

/* Parametri dell'agente VF: pausa fra una richiesta e l'altra, tetto giornaliero e
   pianificazione settimanale (come l'agente SalesMate).

   Sono i freni che tengono lontano un altro blocco del portale Vodafone (18/09/2026: punta
   di 30 verifiche al minuto e 3.668 in una sola giornata). Si apre dalla gestione agenti,
   col pulsante accanto a quello della priorità. */
export default function VfOpzioniDialog({ open, onClose }) {
  const [ritmo, setRitmo] = useState(null);
  const [soloPronti, setSoloPronti] = useState(true);
  const [quanti, setQuanti] = useState(null);     // {pronti, tutto}
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/gea/stato`)
      .then((r) => {
        setRitmo(r.data?.ritmo || null);
        setSoloPronti(r.data?.solo_pronti !== false);
        setQuanti({ pronti: r.data?.eligibili_pronti, tutto: r.data?.eligibili });
      })
      .catch(() => {});
  }, []);
  useEffect(() => { if (open) { setRitmo(null); load(); } }, [open, load]);

  if (!open) return null;

  const piano = ritmo?.pianificazione || {};
  const giorno = (k) => piano[k] || GIORNO_DEFAULT;
  const setGiorno = (k, patch) => setRitmo((s) => ({
    ...s, pianificazione: { ...(s?.pianificazione || {}), [k]: { ...((s?.pianificazione || {})[k] || GIORNO_DEFAULT), ...patch } },
  }));

  const salva = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/gea/opzioni`, {
        pausa: ritmo?.pausa, tetto: ritmo?.tetto, pianificazione: ritmo?.pianificazione,
        solo_pronti: soloPronti,
      });
      setRitmo(r.data?.ritmo || ritmo);
      toast.success('Parametri AI VF salvati');
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore salvataggio'); }
    setBusy(false);
  };

  return createPortal(
    <div className="confirm-overlay" style={{ zIndex: 12500 }} onClick={() => !busy && onClose()}>
      <div className="confirm-dialog" style={{ maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
        <div className="confirm-title">Parametri AI VF · Vodafone</div>
        <div style={{ fontSize: 12.5, color: '#64748b', marginBottom: 12 }}>
          Il portale Vodafone è pubblico e gratuito: si va piano di proposito. Una verifica alla
          volta, con una pausa fra l’una e l’altra variata a caso, un tetto al giorno e le fasce
          orarie qui sotto. Fuori fascia l’agente va in attesa e riprende da solo.
        </div>

        {!ritmo ? (
          <div style={{ color: '#64748b', fontSize: 13 }}>Carico…</div>
        ) : (
          <>
            {/* Su CHI lavora: e' la leva piu' forte contro un nuovo blocco. Tutto l'archivio
                sono ~17.000 aziende; i clienti pronti da lavorare qualche centinaio. */}
            <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: '10px 12px',
                          marginBottom: 14, background: '#f8fafc' }}>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                <input type="checkbox" checked={soloPronti} style={{ marginTop: 3 }}
                       onChange={(e) => setSoloPronti(e.target.checked)} />
                <span>
                  <b>Solo i clienti pronti da lavorare</b>
                  {quanti?.pronti != null && (
                    <> — <b>{quanti.pronti.toLocaleString('it-IT')}</b> da verificare
                      {quanti.tutto != null && <> invece di {quanti.tutto.toLocaleString('it-IT')}</>}
                    </>
                  )}
                  <div style={{ color: '#64748b', fontSize: 12, marginTop: 2 }}>
                    Il dato Vodafone serve quando il cliente si lavora davvero: verificando solo
                    chi è pronto per l’export si fanno poche decine di richieste al giorno invece
                    di migliaia. È la ragione per cui il portale ci ha bloccati.
                  </div>
                </span>
              </label>
            </div>

            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 14 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                Pausa
                <input type="number" min={2} max={120} value={ritmo.pausa} style={{ width: 72 }}
                       onChange={(e) => setRitmo({ ...ritmo, pausa: Number(e.target.value) })} />
                <span style={{ color: '#64748b', fontSize: 12 }}>secondi</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                Tetto
                <input type="number" min={10} max={5000} value={ritmo.tetto} style={{ width: 84 }}
                       onChange={(e) => setRitmo({ ...ritmo, tetto: Number(e.target.value) })} />
                <span style={{ color: '#64748b', fontSize: 12 }}>verifiche al giorno</span>
              </label>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {GIORNI.map(([k, lbl]) => {
                const g = giorno(k);
                return (
                  <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, width: 108 }}>
                      <input type="checkbox" checked={!!g.attivo}
                             onChange={(e) => setGiorno(k, { attivo: e.target.checked })} />
                      {lbl}
                    </label>
                    <input type="time" value={g.dalle} disabled={!g.attivo}
                           onChange={(e) => setGiorno(k, { dalle: e.target.value })} />
                    <span>–</span>
                    <input type="time" value={g.alle} disabled={!g.attivo}
                           onChange={(e) => setGiorno(k, { alle: e.target.value })} />
                    <button type="button" className="btn" style={{ fontSize: 11, padding: '2px 8px' }} disabled={busy}
                            onClick={() => setGiorno(k, { attivo: true, dalle: '00:00', alle: '23:59' })}>
                      Tutto il giorno
                    </button>
                  </div>
                );
              })}
            </div>
          </>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={busy}>Annulla</button>
          <button className="btn btn-primary" onClick={salva} disabled={busy || !ritmo}>
            {busy ? 'Salvo…' : 'Salva'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
